
import React from 'react';

const About: React.FC = () => {
  return (
    <div className="max-w-7xl mx-auto px-6 py-12 md:py-16 space-y-32">
      {/* Section: Our Team */}
      <section className="flex flex-col items-center text-center space-y-12">
        <div className="animate-in fade-in slide-in-from-top-4 duration-700">
          <h1 className="text-6xl md:text-[9rem] font-black leading-[0.85] tracking-tighter serif-heading !text-grow-green max-w-5xl mx-auto">
            Our <span className="italic">Team</span>
          </h1>
        </div>

        {/* Team Hero Image */}
        <div className="w-full animate-in fade-in zoom-in duration-700 delay-150">
          <div className="relative group">
            <div className="absolute -inset-4 bg-emerald-200/20 blur-3xl rounded-[64px] transition-all group-hover:bg-emerald-200/40"></div>
            <img 
              src="https://i.imgur.com/sKTdycZ.jpeg" 
              alt="Grow Local JA Team 25" 
              className="relative z-10 w-full rounded-[48px] shadow-2xl object-cover aspect-[4/3] md:aspect-[16/9] transition-transform duration-700 group-hover:scale-[1.01]"
            />
            <div className="absolute bottom-8 right-8 z-20 hidden md:block">
              <span className="px-6 py-3 bg-white/90 backdrop-blur-md rounded-2xl text-[10px] font-black uppercase tracking-[0.2em] text-grow-green shadow-xl border border-white">
                Team 25 • Junior Achievement
              </span>
            </div>
          </div>
        </div>
      </section>

      {/* Section 1: What is Grow Local? */}
      <section className="flex flex-col lg:flex-row items-center gap-16">
        <div className="flex-1 space-y-8 animate-in fade-in slide-in-from-left-8 duration-700">
          <div className="space-y-4">
            <h2 className="text-6xl md:text-8xl font-black leading-[0.85] tracking-tighter serif-heading !text-grow-green text-left">
              What is <span className="italic">Grow Local?</span>
            </h2>
          </div>
          <p className="text-xl md:text-2xl text-emerald-900/70 leading-relaxed font-medium text-left">
            Grow Local is an all-in-one home gardening kit designed to make growing food simple, fun, and accessible for everyone. Our kits combine hands-on gardening with digital learning to help beginners build confidence while learning how plants grow from seed to stem.
          </p>
        </div>
        <div className="flex-1 animate-in fade-in zoom-in duration-700 delay-150">
          <div className="relative group">
            <div className="absolute -inset-4 bg-emerald-200/20 blur-3xl rounded-[64px]"></div>
            <div className="relative z-10 w-full rounded-[48px] shadow-2xl overflow-hidden aspect-square transition-transform duration-700 group-hover:scale-[1.02]">
              <img 
                src="https://i.imgur.com/GCKkGKj.png" 
                alt="What is Grow Local - Kits and Digital Learning" 
                className="w-full h-full object-cover"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Section 2: What’s in each kit? */}
      <section className="grid lg:grid-cols-2 gap-20 items-start">
        <div className="space-y-12 animate-in fade-in slide-in-from-left-8 duration-700">
          <div className="space-y-4">
            <h2 className="text-4xl md:text-5xl font-black serif-heading !text-grow-green leading-tight text-center">
              Each kit includes everything you need to get started:
            </h2>
          </div>

          <div className="space-y-4">
            {[
              { 
                icon: "🌱", 
                text: "Your choice of Spinach or Pepper Cress seeds to start your grow." 
              },
              { 
                icon: "🏺", 
                text: "An 8-inch reusable pot and pre-mixed high-quality soil." 
              },
              { 
                icon: "📱", 
                text: "A QR code linking to detailed growing guides and expert tips." 
              },
              { 
                icon: "🎮", 
                text: "Learn how seeds activate, roots grow, and stems strengthen through play." 
              }
            ].map((item, idx) => (
              <div 
                key={idx}
                className="flex items-center gap-6 p-6 bg-grow-green rounded-[32px] border border-white/10 shadow-xl shadow-grow-green/10 transition-all hover:shadow-grow-green/20 hover:scale-[1.02] group animate-in slide-in-from-left-4"
                style={{ animationDelay: `${idx * 100}ms` }}
              >
                <div className="flex-shrink-0 w-14 h-14 bg-emerald-800/40 rounded-2xl flex items-center justify-center text-3xl shadow-inner transition-transform group-hover:scale-110 group-hover:rotate-3">
                  {item.icon}
                </div>
                <p className="text-lg md:text-xl text-emerald-50 leading-relaxed font-medium">
                  {item.text}
                </p>
              </div>
            ))}
          </div>
        </div>

        <div className="animate-in fade-in zoom-in duration-700 delay-200 lg:sticky lg:top-32">
          <div className="relative">
            <div className="absolute -inset-6 bg-grow-green/5 blur-3xl rounded-[64px]"></div>
            <img 
              src="https://i.imgur.com/70Vz6E0.png" 
              alt="Grow Local kit contents" 
              className="relative z-10 w-full rounded-[40px] shadow-2xl object-cover h-[550px] transition-all hover:shadow-grow-green/20"
            />
          </div>
        </div>
      </section>

      {/* Section 3: Why Grow Local? */}
      <section className="bg-emerald-900 rounded-[64px] p-12 md:p-24 text-white shadow-2xl relative overflow-hidden">
        <div className="absolute top-0 right-0 w-96 h-96 bg-grow-green/20 blur-[120px] rounded-full translate-x-1/2 -translate-y-1/2"></div>
        <div className="absolute bottom-0 left-0 w-64 h-64 bg-emerald-500/10 blur-[100px] rounded-full -translate-x-1/2 translate-y-1/2"></div>
        
        <div className="relative z-10 grid lg:grid-cols-2 gap-20 items-center">
          <div className="space-y-8">
            <div className="space-y-4">
              <h2 className="text-5xl md:text-7xl font-black serif-heading !text-white leading-tight">Why Grow Local?</h2>
            </div>
            
            <div className="space-y-6 text-lg md:text-xl text-emerald-100/70 leading-relaxed font-medium">
              <p>
                Grow Local exists to address food insecurity in Toronto, where the issue is less about physical access to food and more about affordability, knowledge, and long-term sustainability.
              </p>
              <p>
                We take a holistic approach by empowering people to grow fresh produce at home and build practical food skills that support dignity and independence, aligning with <strong className="font-bold">UN SDG 2 (Zero Hunger)</strong>.
              </p>
            </div>
          </div>
          
          <div className="hidden lg:block relative">
            <div className="absolute inset-0 bg-white/5 backdrop-blur-3xl rounded-3xl border border-white/10 -rotate-3 scale-105"></div>
            <div className="relative bg-emerald-950 p-12 rounded-3xl border border-white/5 space-y-8 shadow-2xl">
               <div className="flex items-center gap-6">
                 <div className="w-16 h-16 bg-grow-green rounded-full flex items-center justify-center text-3xl">🌿</div>
                 <div>
                   <h4 className="text-2xl font-bold serif-heading">Sustainable Future</h4>
                   <p className="opacity-50 text-sm uppercase tracking-widest font-black">Holistic Approach</p>
                 </div>
               </div>
               <div className="h-0.5 w-full bg-white/10"></div>
               <div className="grid grid-cols-2 gap-8">
                 <div className="space-y-2">
                   <p className="text-grow-green font-black text-3xl">SDG 2</p>
                   <p className="text-xs opacity-60 font-bold uppercase tracking-widest">Zero Hunger</p>
                 </div>
                 <div className="space-y-2">
                   <p className="text-grow-green font-black text-3xl">Dignity</p>
                   <p className="text-xs opacity-60 font-bold uppercase tracking-widest">Independence</p>
                 </div>
               </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default About;
